import React, { Component } from 'react';
import { Waypoint } from 'react-waypoint';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';

import { setDisplayProductPreview } from '../../store/actions/ui.actions';
import Analytics from '../global/Analytics';

import {
  setSelectedModifiers,
  setLastModifierSelection,
  setSelectedQty,
  setAddOnModifiers,
  fetchProductDetails,
} from '../../store/actions/product.actions';
import {
  getIsProductDataPending,
  getShippingEstimates,
  getProductBreadcrumbs,
  getProductPromotionsLookup,
  getProductCurrentPrice,
  getProductDetails,
  getProductName,
  getProductPriceWithSelectedModifiersInCents,
  getProductOriginalPrice,
  getIsProductSoldOut,
  getIsSetProduct,
} from '../../store/selectors/product.selectors';
import { getUserDetails } from '../../store/selectors/user.selectors';
import { getWindowWidth } from '../../store/selectors/ui.selectors';
import { getIsUsLang } from '../../store/selectors/general.selectors';
import {
  getAutomaticallyAppliedPromotion,
} from '../../store/selectors/cart.selectors';
import { getLocation } from '../../store/selectors/router.selectors';

import AddToCartContainer from './AddToCart/AddToCart';
import ProductGalleryContainer from './ProductGallery';
import ProductPrice from './ProductPrice';
import ProductTitle from './ProductTitle';
import ShippingMessage from './ShippingMessage';
import AffirmMessage from './AffirmMessage';
import { parseBreadcrumbUrl } from '../global/ProductHelpers';
import UniversalLink from '../global/UniversalLink';
import ProductModifiersContainer from './ProductModifiers/ProductModifiers';
import StyleSelectorContainer from './ProductStyleSelector/StyleSelectorContainer';
import BreadcrumbDto from '../../../../shared/graphCms/breadcrumb.dto';
import LinkDto from '../../../../shared/graphCms/link.dto';
import { ProductDto } from '../../types/schema';
import { RootState } from '../../store/store.types';

const mapStateToProps = (state: RootState) => ({
  productDetails: getProductDetails(state),
  isProductDataPending: getIsProductDataPending(state),
  promotions: getProductPromotionsLookup(state),
  currentPrice: getProductCurrentPrice(state),
  priceWithSelectedModifiersInCents: getProductPriceWithSelectedModifiersInCents(state),
  originalPrice: getProductOriginalPrice(state),
  breadcrumbs: getProductBreadcrumbs(state),
  userDetails: getUserDetails(state),
  windowWidth: getWindowWidth(state),
  location: getLocation(state),
  shippingEstimates: getShippingEstimates(state),
  productName: getProductName(state),
  automaticallyAppliedPromotion: getAutomaticallyAppliedPromotion(state),
  isUsLang: getIsUsLang(state),
  soldOut: getIsProductSoldOut(state),
  isSetProduct: getIsSetProduct(state),
});

const mapDispatchToProps = {
  fetchProductDetailsAction: fetchProductDetails,
  setSelectedModifiersAction: setSelectedModifiers,
  setLastModifierSelectionAction: setLastModifierSelection,
  setSelectedQtyAction: setSelectedQty,
  setAddOnModifiersAction: setAddOnModifiers,
  setDisplayProductPreviewAction: setDisplayProductPreview,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type ProductAboveTheFoldProps = ConnectedProps<typeof connector> & {
  breadcrumbs: BreadcrumbDto;
  windowWidth: number;
  automaticallyAppliedPromotion?: Record<string, unknown>;
  isUsLang: boolean;
  productDetails?: ProductDto;
};

export const renderBreadcrumb = (breadcrumb: LinkDto | null) => breadcrumb && (
  <UniversalLink type="Page" to={`${breadcrumb.destinationPage && breadcrumb.destinationPage.url}${breadcrumb.anchor || ''}`}>
    {breadcrumb.text}
  </UniversalLink>
);
export const renderBreadcrumbs = (breadcrumbs: BreadcrumbDto) => breadcrumbs && (
  <p className="pdp-breadcrumbs">
    {renderBreadcrumb(breadcrumbs.productPath)}
    {breadcrumbs.collectionPath ? ` ${<i className="icon icon-arrow-right" />} ${renderBreadcrumb(breadcrumbs.collectionPath)}` : ''}
  </p>
);

const affirmMinimum = 4999;

class ProductAboveTheFold extends Component<ProductAboveTheFoldProps> {
  constructor(props: ProductAboveTheFoldProps) {
    super(props);

    this.updateProductPreview = this.updateProductPreview.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const {
      productDetails,
      userDetails,
    } = this.props;
    const {
      email = '',
    } = userDetails;
    try {
      Analytics.enqueue({
        method: 'item',
        params: {
          event: 'Product Viewed',
          item: productDetails,
          label: 'Product Page Viewed',
          email,
        },
        location: Analytics.getLocation(),
      });
    } catch (e) { /* nothing to do */ }
  }

  /* eslint-disable react/destructuring-assignment */
  componentWillUnmount() {
    this.props.setDisplayProductPreviewAction(false);
    this.props.setSelectedModifiersAction({});
    this.props.setLastModifierSelectionAction({});
    this.props.setSelectedQtyAction(1);
    this.props.setAddOnModifiersAction({});
  }

  updateProductPreview(bool: boolean) {
    this.props.setDisplayProductPreviewAction(bool);
  }
  /* eslint-enable react/destructuring-assignment */

  renderAffirmButton() {
    const { priceWithSelectedModifiersInCents: price, isUsLang } = this.props;

    return (price && isUsLang && price > affirmMinimum) ? (
      <AffirmMessage amount={price.toString()} />
    ) : null;
  }

  renderAddToCart() {
    // TODO: it should be possible to determine the applicable tier on the server instead.
    const {
      productDetails,
      shippingEstimates,
      soldOut,
    } = this.props;
    const shippingEstimate = shippingEstimates?.[productDetails?.sku];

    return (
      <div className="pdp-details-add-to-cart">
        <AddToCartContainer
          showQuantitySelector={true}
        />
        {shippingEstimate ? (
          <div className="pdp-details-info">
            {!soldOut && (
              <>
                <i
                  className="icon icon-free-shipping"
                />
                <ShippingMessage shippingEstimate={shippingEstimate} />
              </>
            )}
          </div>
        ) : null}
        {this.renderAffirmButton()}
      </div>
    );
  }

  renderBundleDiscount() {
    const {
      productDetails,
      isSetProduct,
    } = this.props;

    const calculatedPrice = productDetails?.pricing?.price?.raw;

    return isSetProduct ? (
      <div className="pdp-details-bundle-savings">
        <div className="bundle-savings-subtotal">
          <span>Subtotal</span>
          <span>{productDetails.pricing.originalPrice?.formatted}</span>
        </div>
        <div className="bundle-savings-discount">
          <span>Bundle Discount</span>
          <span>-${productDetails.price - calculatedPrice}</span>
        </div>
        <div className="bundle-savings-total">
          <span>Total</span>
          <span>${calculatedPrice}</span>
        </div>
      </div>
    ) : null;
  }

  render() {
    const {
      breadcrumbs,
      isProductDataPending,
      automaticallyAppliedPromotion,
      productName,
      productDetails,
      promotions,
      currentPrice = '$0',
      originalPrice,
      isUsLang,
      fetchProductDetailsAction,
      location,
    } = this.props;

    return productDetails && (
      <Waypoint
        onEnter={() => this.updateProductPreview(false)}
        onLeave={() => this.updateProductPreview(true)}
      >
        <section className="pdp-atf">
          {renderBreadcrumbs(parseBreadcrumbUrl(breadcrumbs, isUsLang))}
          <ProductGalleryContainer
            hasSaleBadge={Boolean(automaticallyAppliedPromotion)}
          />
          <ProductTitle
            productName={productName}
            productDetails={productDetails}
          />
          <section
            className={classNames(
              'pdp-details',
              `${isProductDataPending ? 'pending' : ''}`,
              {
                sleeper: location?.pathname?.includes('sleeper'),
              },
            )}
          >
            <ProductPrice
              automaticallyAppliedPromotion={automaticallyAppliedPromotion}
              productDetails={productDetails}
              promotions={promotions}
              currentPrice={currentPrice}
              originalPrice={originalPrice}
            />
            {productDetails.productStyles?.length ? (
              <StyleSelectorContainer
                onSelectCb={fetchProductDetailsAction}
                skuParse={productDetails?.skuParse}
              />
            ) : null}
            <ProductModifiersContainer />
            {this.renderBundleDiscount()}
            {this.renderAddToCart()}
          </section>
        </section>
      </Waypoint>
    );
  }
}

export default connector(ProductAboveTheFold);
